import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ZONES,
  DISPATCH_VIEWS,
  DISPATCH_VIEW_STORAGE_KEY,
  getNavForZone,
  getZoneForPath,
  ZONE_STORAGE_KEY,
} from '../utils/zoneNav';
import { useBadgeCounts } from './useBadgeCounts';
import { useMessageStream } from '@shared/messaging/useMessageStream';

const ShellContext = createContext(null);

export const useShell = () => {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell must be used within ShellProvider');
  return ctx;
};

export const ShellProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const badgeCounts = useBadgeCounts();
  useMessageStream({ enabled: true });

  const pathZone = getZoneForPath(location.pathname);
  const [activeZone, setActiveZoneState] = useState(() => {
    const stored = localStorage.getItem(ZONE_STORAGE_KEY);
    if (stored && ZONES[stored]) return stored;
    return pathZone || 'dispatch';
  });

  const [dispatchView, setDispatchViewState] = useState(() => {
    const stored = localStorage.getItem(DISPATCH_VIEW_STORAGE_KEY);
    if (stored && DISPATCH_VIEWS[stored]) return stored;
    return 'calendar';
  });

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [messagePanel, setMessagePanel] = useState({
    open: false,
    technicianId: null,
    technician: null,
    serviceCallId: null,
    serviceCallLabel: null,
  });
  const [invoicingProgress, setInvoicingProgress] = useState(null);

  const FONT_SCALE_KEY = 'hubFontScale';
  const FONT_SIZES = [14, 15, 16, 17, 18, 20];

  const [fontSize, setFontSizeState] = useState(() => {
    const stored = Number(localStorage.getItem(FONT_SCALE_KEY));
    return FONT_SIZES.includes(stored) ? stored : 16;
  });

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
    localStorage.setItem(FONT_SCALE_KEY, String(fontSize));
  }, [fontSize]);

  const increaseFontSize = useCallback(() => {
    setFontSizeState((current) => {
      const idx = FONT_SIZES.indexOf(current);
      if (idx < 0 || idx >= FONT_SIZES.length - 1) return current;
      return FONT_SIZES[idx + 1];
    });
  }, []);

  const decreaseFontSize = useCallback(() => {
    setFontSizeState((current) => {
      const idx = FONT_SIZES.indexOf(current);
      if (idx <= 0) return current;
      return FONT_SIZES[idx - 1];
    });
  }, []);

  useEffect(() => {
    if (pathZone) {
      setActiveZoneState(pathZone);
      localStorage.setItem(ZONE_STORAGE_KEY, pathZone);
    }
  }, [pathZone]);

  const setActiveZone = useCallback((zoneId) => {
    setActiveZoneState(zoneId);
    localStorage.setItem(ZONE_STORAGE_KEY, zoneId);
    const zoneNav = getNavForZone(zoneId);
    const currentInZone = zoneNav.some((item) => item.href === location.pathname);
    if (!currentInZone && zoneNav.length > 0) {
      navigate(zoneNav[0].href);
    }
  }, [location.pathname, navigate]);

  const setDispatchView = useCallback((viewId) => {
    if (!DISPATCH_VIEWS[viewId]) return;
    setDispatchViewState(viewId);
    localStorage.setItem(DISPATCH_VIEW_STORAGE_KEY, viewId);
    if (location.pathname !== '/dispatch') {
      navigate('/dispatch');
    }
  }, [location.pathname, navigate]);

  const closeMessagePanel = useCallback(() => {
    setMessagePanel({
      open: false,
      technicianId: null,
      technician: null,
      serviceCallId: null,
      serviceCallLabel: null,
    });
  }, []);

  const openMessageInbox = useCallback(() => {
    setMessagePanel({
      open: true,
      technicianId: null,
      technician: null,
      serviceCallId: null,
      serviceCallLabel: null,
    });
  }, []);

  const openMessageThread = useCallback(({ technicianId, technician = null, serviceCallId = null, serviceCallLabel = null }) => {
    setMessagePanel({
      open: true,
      technicianId,
      technician,
      serviceCallId,
      serviceCallLabel,
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((open) => !open);
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        setAssistantOpen((open) => !open);
        return;
      }

      if (activeZone !== 'dispatch' || location.pathname !== '/dispatch') return;
      if (e.target.matches('input, textarea, select, [contenteditable]')) return;

      if (e.key === '1') setDispatchView('calendar');
      if (e.key === '2') setDispatchView('board');
      if (e.key === '3') setDispatchView('map');
      if (e.key === '4') setDispatchView('today');
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeZone, location.pathname, setDispatchView]);

  const value = useMemo(() => ({
    activeZone,
    setActiveZone,
    dispatchView,
    setDispatchView,
    commandPaletteOpen,
    setCommandPaletteOpen,
    assistantOpen,
    setAssistantOpen,
    messagePanel,
    openMessageInbox,
    openMessageThread,
    closeMessagePanel,
    badgeCounts,
    invoicingProgress,
    setInvoicingProgress,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    canIncreaseFontSize: fontSize < FONT_SIZES[FONT_SIZES.length - 1],
    canDecreaseFontSize: fontSize > FONT_SIZES[0],
  }), [
    activeZone,
    setActiveZone,
    dispatchView,
    setDispatchView,
    commandPaletteOpen,
    badgeCounts,
    assistantOpen,
    messagePanel,
    openMessageInbox,
    openMessageThread,
    closeMessagePanel,
    invoicingProgress,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
  ]);

  return (
    <ShellContext.Provider value={value}>
      {children}
    </ShellContext.Provider>
  );
};
